import { z } from 'zod';
import { objectIdSchema } from './common.js';
import { nutrientKeySchema, nutritionBasisSchema, nutrientsSchema } from './nutrients.js';
/** Where a food record came from. Drives trust and de-duplication rules. */
export const foodSourceSchema = z.enum([
    /** Fetched from OpenFoodFacts and cached locally. */
    'openfoodfacts',
    /** Shipped with the app: common German staples. */
    'seed',
    /** Entered by a user by hand. */
    'custom',
    /** Extracted from a photographed nutrition label. */
    'label_scan',
]);
/**
 * EAN-8, UPC-A, EAN-13 or GTIN-14. Kept as a string throughout — leading zeros
 * are significant and a 14-digit GTIN overflows exact integer territory.
 */
export const barcodeSchema = z
    .string()
    .trim()
    .regex(/^\d{8}$|^\d{12,14}$/, 'must be an 8, 12, 13 or 14 digit barcode');
export const servingSchema = z.object({
    /** Weight of one serving, in grams (or millilitres for per_100ml foods). */
    grams: z.number().min(0.1).max(5000),
    /** What one serving is called, e.g. "1 Riegel (21 g)". */
    label: z.string().trim().min(1).max(80),
});
export const foodSchema = z.object({
    id: objectIdSchema,
    source: foodSourceSchema,
    barcode: z.string().nullable(),
    name: z.string(),
    brand: z.string().nullable(),
    basis: nutritionBasisSchema,
    nutrients: nutrientsSchema,
    serving: servingSchema.nullable(),
    /**
     * Grams per millilitre, used to convert volumetric quantities (EL, ml) for a
     * food whose nutrients are stated per 100 g. Defaults to 1.0.
     */
    density: z.number().min(0.1).max(3).nullable(),
    /** Set when the food is private to one user; null means shared/global. */
    ownerId: objectIdSchema.nullable(),
    /**
     * Nutrients that were *not* declared on the product and were filled in from
     * an estimate. Only OpenFoodFacts' `nutriments_estimated` block feeds this —
     * values computed from the ingredient list against the CIQUAL food-composition
     * database, never a model's recollection.
     *
     * Carried so the UI can say which numbers are inferred; an estimate that
     * looks identical to a measured value is worse than a visible gap.
     */
    estimatedNutrients: z.array(nutrientKeySchema).default([]),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createFoodSchema = z.object({
    name: z.string().trim().min(1).max(200),
    brand: z.string().trim().max(120).nullable().optional(),
    barcode: barcodeSchema.nullable().optional(),
    basis: nutritionBasisSchema.default('per_100g'),
    nutrients: nutrientsSchema,
    serving: servingSchema.nullable().optional(),
    density: z.number().min(0.1).max(3).nullable().optional(),
});
export const updateFoodSchema = createFoodSchema
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'no fields to update');
export const foodSearchQuerySchema = z.object({
    q: z.string().trim().min(1).max(120),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    /**
     * Whether to reach out to OpenFoodFacts when the local catalog is thin.
     * The PWA leaves this on for explicit searches and off for autocomplete.
     */
    remote: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((value) => value === true || value === 'true')
        .default(false),
});
export const barcodeParamSchema = z.object({ barcode: barcodeSchema });
//# sourceMappingURL=food.js.map